import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Typography,
  CircularProgress,
  Divider,
  Tooltip,
  Checkbox
} from '@mui/material';
import {
  Close as CloseIcon,
  Print as PrintIcon,
  Description as ExcelIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import ExcelJS from 'exceljs';
import { useTranslation } from 'react-i18next';
import InvoicePrintTemplate from './InvoicePrintTemplate';
import { executePrint } from '../../../../utils/print/executePrint';
import DownloadIndicator from '../../../../components/common/DownloadIndicator';
import dayjs from 'dayjs';
import signImage from '../../../../assets/sign.jpg';
import { COMPANY } from '../../../../constants/companyConstants';
import api from '../../../../api/api';
import { fetchItemListCached, getItemListCached } from '../../../../utils/cache/itemCache';
import { fetchBuyerListCached, getBuyerListCached } from '../../../../utils/cache/buyerCache';
import { fetchDeliveryListCached, getDeliveryListCached } from '../../../../utils/cache/deliveryCache';
import { fetchManagerListCached, getManagerListCached } from '../../../../utils/cache/managerCache';
import { fetchVendorListCached, getVendorListCached } from '../../../../utils/cache/vendorCache';
import { orderService } from '../../../../api/services/orderService';
import {
  parseSizeFromItemText,
  formatSizeValue,
  removeSizeFromItemText,
  getFontSizeForLength,
  getCurrencySymbol,
  formatDateForPrint,
  formatCurrencyValue,
  makeTdStyle,
  TH_STYLE_STRING,
  computeUniformFontSize,
  computeInvoiceTotals,
  imageToBase64DataUrl,
  imageToBase64Raw,
  COLUMN_OPTIONS,
} from '../utils/invoicePrintUtils';

const ITEM_ARRAY_SCORE_KEYS = [
  'sub_order_id',
  'item_id',
  'quantity',
  'unit_price',
  'price',
  'color',
  'style_no',
  'file_no'
];

const scoreItemArray = (value) => {
  if (!Array.isArray(value) || value.length === 0) return -1;
  const first = value[0];
  if (!first || typeof first !== 'object') return -1;
  return ITEM_ARRAY_SCORE_KEYS.reduce((score, key) => (key in first ? score + 1 : score), 0);
};

const tryParseJson = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
};

const pickBestItemsArray = (raw, base) => {
  const candidates = [];
  const pushCandidate = (key, value) => {
    const score = scoreItemArray(value);
    if (score >= 0) {
      candidates.push({ key, value, score });
    }
  };

  [
    ['base.items', base?.items],
    ['base.invoice_items', base?.invoice_items],
    ['base.lines', base?.lines],
    ['base.details', base?.details],
    ['base.item_list', base?.item_list],
    ['base.invoice_item_list', base?.invoice_item_list],
    ['raw.items', raw?.items],
    ['raw.invoice_items', raw?.invoice_items],
    ['raw.lines', raw?.lines],
    ['raw.details', raw?.details],
    ['raw.item_list', raw?.item_list],
    ['raw.invoice_item_list', raw?.invoice_item_list]
  ].forEach(([key, value]) => pushCandidate(key, value));

  Object.entries(raw || {}).forEach(([key, value]) => {
    pushCandidate(`raw.${key}`, value);
  });

  Object.entries(raw || {}).forEach(([outerKey, outerValue]) => {
    if (!outerValue || typeof outerValue !== 'object' || Array.isArray(outerValue)) return;
    Object.entries(outerValue).forEach(([innerKey, innerValue]) => {
      pushCandidate(`raw.${outerKey}.${innerKey}`, innerValue);
    });
  });

  const visited = new WeakSet();
  const walk = (node, path, depth = 0) => {
    if (depth > 2 || node === null || node === undefined) return;
    if (Array.isArray(node)) {
      pushCandidate(path, node);
      node.forEach((entry, idx) => {
        walk(entry, `${path}[${idx}]`, depth + 1);
      });
      return;
    }
    if (typeof node === 'string') {
      const parsed = tryParseJson(node);
      if (parsed) walk(parsed, `${path}(parsed)`, depth + 1);
      return;
    }
    if (typeof node === 'object') {
      if (visited.has(node)) return;
      visited.add(node);
      Object.entries(node).forEach(([k, v]) => {
        walk(v, `${path}.${k}`, depth + 1);
      });
    }
  };
  walk(raw, 'raw');

  if (!candidates.length) {
    return { items: [], source: 'not-found' };
  }

  candidates.sort((a, b) => b.score - a.score);
  return { items: candidates[0].value, source: candidates[0].key };
};

const createBoundedLogger = () => {
  const noop = () => {};
  return { log: noop, logOnce: noop };
};

const logger = createBoundedLogger();

const normalizeInvoiceDetailPayload = (payload, fallbackInvoice = {}) => {
  const raw = payload?.data ?? payload;
  const base = raw?.invoice || raw?.header || raw?.result || raw || {};
  const { items, source } = pickBestItemsArray(raw, base);

  logger.logOnce('detail items source picked', {
    invoiceId: base?.invoice_id ?? fallbackInvoice?.invoice_id,
    source,
    itemCount: items.length,
    sampleKeys: items[0] && typeof items[0] === 'object' ? Object.keys(items[0]) : []
  });

  return { ...fallbackInvoice, ...base, items };
};

const buildLookupMaps = ({ items, buyers, deliveries, managers, vendors }) => {
  const itemMap = new Map();
  (items || []).forEach((item) => {
    const itemName = item.name || item.item_name || '';
    const itemSpec = item.specification || item.size_name || '';
    [item.item_id, item.thread_id, item.id].forEach((id) => {
      if (id != null && !itemMap.has(String(id))) {
        itemMap.set(String(id), { itemName, itemSpec });
      }
    });
  });

  const buyerMap = new Map();
  (buyers || []).forEach((buyer) => {
    const buyerName = buyer.partner_name || buyer.buyer_name || buyer.partner_group || buyer.name || '';
    [buyer.partner_id, buyer.buyer_id].forEach((id) => {
      if (id != null && !buyerMap.has(String(id))) {
        buyerMap.set(String(id), buyerName);
      }
    });
  });

  const deliveryMap = new Map();
  (deliveries || []).forEach((delivery) => {
    const deliveryName = delivery.delivery_name || delivery.name || '';
    if (delivery.delivery_id != null && !deliveryMap.has(String(delivery.delivery_id))) {
      deliveryMap.set(String(delivery.delivery_id), deliveryName);
    }
  });

  const managerMap = new Map();
  (managers || []).forEach((manager) => {
    const managerName = manager.manager_name || manager.name || '';
    [manager.manager_id, manager.id].forEach((id) => {
      if (id != null && !managerMap.has(String(id))) {
        managerMap.set(String(id), managerName);
      }
    });
  });

  const vendorMap = new Map();
  (vendors || []).forEach((vendor) => {
    const vendorName = vendor.partner_name || vendor.vendor_name || vendor.company_name || '';
    [vendor.vendor_id, vendor.partner_id, vendor.company_id].forEach((id) => {
      if (id != null && !vendorMap.has(String(id))) {
        vendorMap.set(String(id), vendorName);
      }
    });
  });

  return { itemMap, buyerMap, deliveryMap, managerMap, vendorMap };
};

const toDateOnly = (value) => {
  if (!value) return null;
  const d = dayjs(value);
  return d.isValid() ? d.format('YYYY-MM-DD') : null;
};

const buildCandidateParams = (startDate, endDate) => ({
  start_date: startDate,
  end_date: endDate,
  start_date_with_timezone: dayjs(startDate).startOf('day').format('YYYY-MM-DDTHH:mm:ssZ'),
  end_date_with_timezone: dayjs(endDate).endOf('day').format('YYYY-MM-DDTHH:mm:ssZ'),
  client_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
});

const enrichCandidatesAsItems = (candidates, maps) => {
  return (candidates || []).map((candidate) => {
    const itemInfo = maps.itemMap.get(String(candidate.item_id)) || {};
    const quantity = Number(candidate.qty_delivered ?? candidate.qty ?? candidate.qty_requested ?? 0);
    const unitPrice = Number(candidate.price ?? 0);

    return {
      sub_order_id: candidate.sub_order_id,
      manager_name: maps.managerMap.get(String(candidate.manager_id)) || '',
      buyer: maps.buyerMap.get(String(candidate.buyer_id)) || (candidate.buyer_id ? String(candidate.buyer_id) : ''),
      style_no: candidate.style_no || '',
      file_no: candidate.file_no || '',
      factory: maps.deliveryMap.get(String(candidate.delivery_id)) || '',
      delivery_date: candidate.existing_invoice_date || candidate.time_of_arrival || null,
      thread_name: itemInfo.itemName || '',
      size_name: itemInfo.itemSpec || '',
      color: candidate.color || '',
      quantity,
      unit_price: unitPrice,
      price: unitPrice,
      amount: quantity * unitPrice,
      vat_amount: 0,
      vat_amount2: 0
    };
  });
};

let cachedSignBase64 = null;
const getSignBase64 = async () => {
  if (cachedSignBase64) return cachedSignBase64;
  try {
    cachedSignBase64 = await imageToBase64DataUrl(signImage);
    return cachedSignBase64;
  } catch (e) {
    console.error('Sign image load error:', e);
    return '';
  }
};

const generatePrintPageHtml = (invoice, visibleColumns, signBase64) => {
  const items = invoice.items || [];
  const { totalQty, subTotal, totalVat } = computeInvoiceTotals(items);
  const sym = getCurrencySymbol(invoice.currency);
  const uniformFs = computeUniformFontSize(items, invoice, visibleColumns);
  const v = (val) => String(val || '-');

  const printColDefs = [
    { key: '_no', label: 'NO', minCh: 3, align: 'center', visible: true,
      getText: (_it, idx) => String(idx + 1) },
    { key: 'buyer', label: 'BUYER', minCh: 8, align: 'center', visible: visibleColumns.buyer !== false,
      getText: (it) => v(it.buyer) },
    { key: 'styleNo', label: 'STYLE NO', minCh: 10, align: 'center', visible: visibleColumns.styleNo !== false,
      getText: (it) => v(it.style_no || it.style_name) },
    { key: 'fileNo', label: 'FILE NO', minCh: 14, align: 'center', visible: visibleColumns.fileNo !== false,
      getText: (it) => v(it.file_no) },
    { key: 'managerName', label: 'MANAGER', minCh: 12, align: 'center', visible: visibleColumns.managerName !== false,
      getText: (it) => v(it.manager_name) },
    { key: 'factory', label: 'FACTORY', minCh: 10, align: 'center', visible: visibleColumns.factory !== false,
      getText: (it) => v(it.factory) },
    { key: 'deliveryDate', label: 'DELIVERY DATE', minCh: 12, align: 'center', visible: visibleColumns.deliveryDate !== false,
      getText: (it) => formatDateForPrint(it.delivery_date || it.date) },
    { key: 'item', label: 'ITEM', minCh: 12, align: 'center', visible: visibleColumns.item !== false,
      getText: (it) => removeSizeFromItemText(it.thread_name || it.item_description || '') },
    { key: 'size', label: 'SIZE', minCh: 6, align: 'center', visible: visibleColumns.size !== false,
      getText: (it) => parseSizeFromItemText(it.thread_name || it.item_description || '') || formatSizeValue(it.size_name || it.size) || '-' },
    { key: 'color', label: 'COLOR', minCh: 12, align: 'center', visible: visibleColumns.color !== false,
      getText: (it) => v(it.color) },
    { key: 'qty', label: 'QTY', minCh: 6, align: 'center', visible: visibleColumns.qty !== false,
      getText: (it) => it.quantity ? Number(it.quantity).toLocaleString() : '-' },
    { key: 'price', label: 'PRICE', minCh: 10, align: 'right', visible: visibleColumns.price !== false,
      getText: (it) => (it.price || it.unit_price) ? sym + ' ' + formatCurrencyValue(it.price || it.unit_price) : '-' },
    { key: 'vat', label: 'VAT', minCh: 8, align: 'right', visible: visibleColumns.vat !== false,
      getText: (it) => {
        const va = Number(it.vat_amount || 0) + Number(it.vat_amount2 || 0);
        return va ? sym + ' ' + formatCurrencyValue(va) : '-';
      } },
    { key: 'amount', label: 'AMOUNT', minCh: 12, align: 'right', visible: visibleColumns.amount !== false,
      getText: (it) => it.amount ? sym + ' ' + formatCurrencyValue(it.amount) : '-' }
  ];

  const visibleCols = printColDefs.filter((c) => c.visible);

  const maxChars = visibleCols.map((col) => {
    let max = col.label.length;
    items.forEach((it, i) => {
      const t = col.getText(it, i) || '';
      if (t.length > max) max = t.length;
    });
    return Math.max(col.minCh, Math.min(60, max));
  });
  const totalChars = maxChars.reduce((s, n) => s + n, 0) || 1;
  const colWidths = maxChars.map((c) => `${((c / totalChars) * 100).toFixed(2)}%`);

  const colGroupHtml = `<colgroup>${colWidths.map((w) => `<col style="width:${w}" />`).join('')}</colgroup>`;
  const headerCols = visibleCols.map((c) => `<th style="${TH_STYLE_STRING}">${c.label}</th>`);

  const rows = items.map((item, idx) => {
    const cols = visibleCols.map((col) => {
      const t = col.getText(item, idx);
      const styleStr = makeTdStyle(uniformFs, t, col.align);
      return `<td style="${styleStr}">${t}</td>`;
    });
    return `<tr>${cols.join('')}</tr>`;
  }).join('');

  let totalsRows = '';
  if (visibleColumns.qty !== false) {
    totalsRows += `<tr><td style="padding:3px 8px;font-size:10px;text-align:left;border-bottom:1px solid #e0e0e0">TOTAL Q'TY</td><td style="padding:3px 8px;font-size:10px;text-align:right;border-bottom:1px solid #e0e0e0">${totalQty.toLocaleString()}</td></tr>`;
  }
  if (visibleColumns.amount !== false) {
    totalsRows += `<tr><td style="padding:3px 8px;font-size:10px;text-align:left;border-bottom:1px solid #e0e0e0">SUB TOTAL</td><td style="padding:3px 8px;font-size:10px;text-align:right;border-bottom:1px solid #e0e0e0">${sym} ${formatCurrencyValue(subTotal)}</td></tr>`;
  }
  if (visibleColumns.vat !== false) {
    totalsRows += `<tr><td style="padding:3px 8px;font-size:10px;text-align:left;border-bottom:1px solid #e0e0e0">VAT</td><td style="padding:3px 8px;font-size:10px;text-align:right;border-bottom:1px solid #e0e0e0">${sym} ${formatCurrencyValue(totalVat)}</td></tr>`;
  }
  if (visibleColumns.amount !== false) {
    totalsRows += `<tr><td style="padding:4px 8px;font-size:11px;font-weight:bold;text-align:left;border-top:1px solid #000;border-bottom:2px double #000">TOTAL</td><td style="padding:4px 8px;font-size:11px;font-weight:bold;text-align:right;border-top:1px solid #000;border-bottom:2px double #000">${sym} ${formatCurrencyValue(subTotal + totalVat)}</td></tr>`;
  }

  return `
    <div class="invoice-page">
      <div style="text-align:center;margin-bottom:16px">
        <div style="font-size:18px;font-weight:bold;margin-bottom:4px">${COMPANY.name}</div>
        <div style="font-size:11px;margin-bottom:2px">${COMPANY.address}</div>
        <div style="font-size:11px;margin-bottom:12px">${COMPANY.phoneLabel}</div>
        <div style="font-size:24px;font-weight:bold;margin-bottom:8px">INVOICE</div>
        <div style="margin-bottom:16px">
          <div style="display:flex;justify-content:space-between;margin-bottom:3px">
            ${visibleColumns.customerName !== false ? `<div style="font-weight:bold;font-size:11px">CUSTOMER : ${invoice.customer_name || '-'}</div>` : '<div></div>'}
            ${visibleColumns.invoiceNo !== false ? `<div style="font-weight:bold;font-size:11px">INVOICE NO : ${invoice.invoice_no || '-'}</div>` : '<div></div>'}
          </div>
          <div style="display:flex;justify-content:space-between">
            ${visibleColumns.customerName !== false ? `<div style="font-size:10px">${invoice.customer_address || '-'}</div>` : '<div></div>'}
            ${visibleColumns.invoiceDate !== false ? `<div style="font-size:11px;font-weight:bold">Issued Date : ${formatDateForPrint(invoice.invoice_date)}</div>` : '<div></div>'}
          </div>
        </div>
      </div>
      <table style="width:100%;border-collapse:collapse;margin-bottom:20px;table-layout:auto">
        ${colGroupHtml}
        <thead><tr>${headerCols.join('')}</tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div style="display:flex;justify-content:space-between;margin-top:16px;gap:32px">
        <div style="flex:1">
          <div style="font-size:10px;font-weight:bold;margin-bottom:2px">At bank : ${COMPANY.bank.name}</div>
          <div style="font-size:10px;font-weight:bold;margin-bottom:2px">Bank Acc ($) : ${COMPANY.bank.accountUSD} SWIFT CODE : ${COMPANY.bank.swiftCode}</div>
          <div style="font-size:10px;font-weight:bold;margin-bottom:2px">Bank Acc (Rp) : ${COMPANY.bank.accountIDR} SWIFT CODE : ${COMPANY.bank.swiftCode}</div>
          <div style="font-size:10px;font-weight:bold">ACC NAME : ${COMPANY.bank.accountName}</div>
        </div>
        <div style="min-width:200px;max-width:250px">
          <table style="width:100%;border-collapse:collapse">
            <tbody>${totalsRows}</tbody>
          </table>
          ${signBase64 && visibleColumns.sign !== false ? `<div style="margin-top:16px;text-align:center"><img src="${signBase64}" style="height:80px;width:auto;object-fit:contain;margin-left:20px" /></div>` : ''}
        </div>
      </div>
    </div>
  `;
};

const PRINT_HTML_WRAPPER = (pages) => `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Invoice</title>
  <style>
    @page { size: A4 portrait; margin: 0; }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: "Malgun Gothic", "맑은 고딕", sans-serif; font-size: 8px; line-height: 1.4; color: #000; background: white; }
    .invoice-page { padding: 15mm 20mm; page-break-after: always; page-break-inside: avoid; }
    .invoice-page:last-child { page-break-after: auto; }
  </style>
</head>
<body>${pages}</body>
</html>`;

const EXCEL_HEADER_FONT = Object.freeze({ name: '맑은 고딕', size: 8, bold: false });
const EXCEL_THIN_BORDER = Object.freeze({
  top: { style: 'thin' },
  left: { style: 'thin' },
  bottom: { style: 'thin' },
  right: { style: 'thin' }
});
const EXCEL_CENTER_ALIGN = Object.freeze({ horizontal: 'center', vertical: 'middle' });

const createExcelCellSetter = (uniformFs) => (cell, value, align = 'center') => {
  cell.value = value;
  cell.font = { name: '맑은 고딕', size: uniformFs };
  cell.alignment = { horizontal: align, vertical: 'middle', wrapText: true };
  cell.border = EXCEL_THIN_BORDER;
};

const InvoicePrintModal = ({
  open,
  onClose,
  selectedInvoices,
  invoiceData,
  initialColumns = {}
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isDataReady, setIsDataReady] = useState(false);
  const [columnSettingsOpen, setColumnSettingsOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState({});
  const [currentPage, setCurrentPage] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [detailedInvoiceMap, setDetailedInvoiceMap] = useState({});
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  const columnSettingsRef = useRef(null);
  const columnSettingsButtonRef = useRef(null);
  const downloadIndicatorRef = useRef();
  const signImageCache = useRef(null);
  const printRef = useRef(null);

  const mergedInvoiceData = useMemo(() => {
    const map = new Map();
    (invoiceData || []).forEach((invoice) => {
      map.set(invoice.invoice_id, invoice);
    });
    Object.entries(detailedInvoiceMap).forEach(([key, invoice]) => {
      map.set(Number(key), {
        ...(map.get(Number(key)) || {}),
        ...invoice
      });
    });
    return Array.from(map.values());
  }, [invoiceData, detailedInvoiceMap]);

  useEffect(() => {
    if (!open) return;
    logger.logOnce('open with selected invoices', {
      selectedInvoices,
      selectedInvoiceTypes: (selectedInvoices || []).map((id) => typeof id),
      invoiceDataCount: (invoiceData || []).length,
      mergedInvoiceDataCount: (mergedInvoiceData || []).length
    });
  }, [open, selectedInvoices, invoiceData, mergedInvoiceData]);

  useEffect(() => {
    if (!open || !selectedInvoices?.length) return;
    let isActive = true;

    const fetchInvoiceDetails = async () => {
      setIsFetchingDetails(true);
      try {
        let itemCache = getItemListCached() || [];
        let buyerCache = getBuyerListCached() || [];
        let deliveryCache = getDeliveryListCached() || [];
        let managerCache = getManagerListCached() || [];
        let vendorCache = getVendorListCached() || [];

        const shouldFetchCache =
          itemCache.length === 0 ||
          buyerCache.length === 0 ||
          deliveryCache.length === 0 ||
          managerCache.length === 0 ||
          vendorCache.length === 0;

        if (shouldFetchCache) {
          await Promise.all([
            fetchItemListCached(false),
            fetchBuyerListCached(false),
            fetchDeliveryListCached(false),
            fetchManagerListCached(false),
            fetchVendorListCached(false)
          ]);
          itemCache = getItemListCached() || [];
          buyerCache = getBuyerListCached() || [];
          deliveryCache = getDeliveryListCached() || [];
          managerCache = getManagerListCached() || [];
          vendorCache = getVendorListCached() || [];
        }

        const maps = buildLookupMaps({
          items: itemCache,
          buyers: buyerCache,
          deliveries: deliveryCache,
          managers: managerCache,
          vendors: vendorCache
        });

        const detailResponses = await Promise.all(
          selectedInvoices.map(async (invoiceId) => {
            const fallbackInvoice = (invoiceData || []).find((invoice) => invoice.invoice_id === invoiceId) || {};
            try {
              logger.logOnce('requesting invoice detail', { invoiceId, type: typeof invoiceId });
              const response = await api.get(`/invoice/v1/invoice/item/${invoiceId}/with-items`);
              logger.logOnce('invoice detail raw response', {
                invoiceId,
                topLevelKeys: response?.data ? Object.keys(response.data) : [],
                hasDataField: Boolean(response?.data?.data)
              });
              const detail = normalizeInvoiceDetailPayload(response?.data, fallbackInvoice);
              const customerId = detail.customer_id ?? detail.vendor_id;

              const enrichedItems = (detail.items || []).map((item) => {
                const itemId = item.item_id ?? item.thread_id;
                const buyerId = item.buyer_id;
                const deliveryId = item.delivery_id;
                const managerIdFromItem = item.manager_id;
                const itemInfo = maps.itemMap.get(String(itemId)) || {};
                const threadName = item.thread_name || item.item_name || item.item_description || itemInfo.itemName || '';
                const parsedSize = parseSizeFromItemText(threadName);

                return {
                  ...item,
                  thread_name: threadName,
                  size_name: parsedSize || item.size_name || item.size || itemInfo.itemSpec || '',
                  buyer: item.buyer || (buyerId != null ? maps.buyerMap.get(String(buyerId)) || String(buyerId) : ''),
                  factory: item.factory || (deliveryId != null ? maps.deliveryMap.get(String(deliveryId)) || '' : ''),
                  manager_name: item.manager_name || (managerIdFromItem != null ? maps.managerMap.get(String(managerIdFromItem)) || '' : ''),
                  file_no: item.file_no || '',
                  delivery_date: item.delivery_date || item.time_of_arrival || item.date || null
                };
              });

              return {
                invoice_id: invoiceId,
                detail: {
                  ...detail,
                  customer_name: detail.customer_name || maps.vendorMap.get(String(customerId)) || fallbackInvoice.customer_name || '',
                  manager_name: detail.manager_name || maps.managerMap.get(String(detail.manager_id)) || fallbackInvoice.manager_name || '',
                  buyer_name: detail.buyer_name || maps.buyerMap.get(String(detail.buyer_id)) || fallbackInvoice.buyer_name || '',
                  items: enrichedItems
                }
              };
            } catch (error) {
              console.error('[InvoicePrintModal] failed to fetch invoice detail', {
                invoiceId,
                message: error?.message,
                status: error?.response?.status,
                data: error?.response?.data
              });
              return {
                invoice_id: invoiceId,
                detail: {
                  ...fallbackInvoice,
                  items: fallbackInvoice.items || []
                }
              };
            }
          })
        );

        if (!isActive) return;
        const nextMap = {};
        detailResponses.forEach(({ invoice_id, detail }) => {
          nextMap[invoice_id] = detail;
        });

        const invoicesNeedingItems = detailResponses.filter(({ detail }) => (detail?.items || []).length === 0);
        if (invoicesNeedingItems.length > 0) {
          try {
            logger.logOnce('fallback candidates request', { mode: 'no-filter' });
            let candidateResponse = await api.get('/invoice/v1/invoice/candidates');
            let candidates = Array.isArray(candidateResponse?.data) ? candidateResponse.data : [];

            if (candidates.length === 0) {
              const broadStartDate = '2000-01-01';
              const broadEndDate = dayjs().add(1, 'year').format('YYYY-MM-DD');
              const candidateParams = buildCandidateParams(broadStartDate, broadEndDate);
              logger.logOnce('fallback candidates request', { mode: 'broad-range', ...candidateParams });
              candidateResponse = await api.get('/invoice/v1/invoice/candidates', { params: candidateParams });
              candidates = Array.isArray(candidateResponse?.data) ? candidateResponse.data : [];
            }

            logger.logOnce('fallback candidates fetched', {
              count: candidates.length,
              sampleExistingInvoiceIds: candidates.slice(0, 5).map((c) => c.existing_invoice_id),
              sampleExistingInvoiceNos: candidates.slice(0, 5).map((c) => c.existing_invoice_no)
            });

            const candidateMapByInvoiceId = candidates.reduce((acc, candidate) => {
              const key = candidate.existing_invoice_id;
              if (!key) return acc;
              if (!acc[key]) acc[key] = [];
              acc[key].push(candidate);
              return acc;
            }, {});

            invoicesNeedingItems.forEach(({ invoice_id, detail }) => {
              const matchedCandidates =
                candidateMapByInvoiceId[invoice_id] ||
                candidates.filter((c) => c.existing_invoice_no && c.existing_invoice_no === detail?.invoice_no);

              if (matchedCandidates.length > 0) {
                const fallbackItems = enrichCandidatesAsItems(matchedCandidates, maps);
                nextMap[invoice_id] = { ...detail, items: fallbackItems };
                logger.logOnce('fallback items applied', { invoice_id, itemCount: fallbackItems.length });
              } else {
                logger.logOnce('fallback items not matched', { invoice_id, invoice_no: detail?.invoice_no });
              }
            });
          } catch (fallbackError) {
            console.error('[InvoicePrintModal] fallback candidates fetch failed', {
              message: fallbackError?.message,
              status: fallbackError?.response?.status,
              data: fallbackError?.response?.data
            });
          }
        }

        try {
          const allSubOrderIds = [
            ...new Set(
              Object.values(nextMap)
                .flatMap((d) => (d?.items || []).map((it) => it?.sub_order_id))
                .filter((id) => id != null && id !== '')
                .map((id) => Number(id))
                .filter((n) => Number.isFinite(n))
            )
          ];

          if (allSubOrderIds.length > 0) {
            const subOrderList = await orderService.getSubOrderListByIds(allSubOrderIds);
            const subOrderMap = new Map();
            (subOrderList || []).forEach((so) => {
              if (so?.sub_order_id != null) {
                subOrderMap.set(String(so.sub_order_id), so);
              }
            });

            Object.keys(nextMap).forEach((key) => {
              const detail = nextMap[key];
              if (!detail?.items) return;
              detail.items = detail.items.map((it) => {
                const so = it.sub_order_id != null ? subOrderMap.get(String(it.sub_order_id)) : null;
                if (!so) return it;
                const soManagerId = so.manager_id != null ? String(so.manager_id) : null;
                const soBuyerId = so.buyer_id != null ? String(so.buyer_id) : null;
                const soDeliveryId = so.delivery_id != null ? String(so.delivery_id) : null;
                const lookedUpManager = soManagerId ? maps.managerMap.get(soManagerId) : '';
                const lookedUpBuyer = soBuyerId ? maps.buyerMap.get(soBuyerId) : '';
                const lookedUpFactory = soDeliveryId ? maps.deliveryMap.get(soDeliveryId) : '';
                return {
                  ...it,
                  file_no: it.file_no || so.file_no || '',
                  style_no: it.style_no || so.style_no || '',
                  color: it.color || so.color || '',
                  manager_id: it.manager_id ?? so.manager_id ?? null,
                  manager_name: it.manager_name || lookedUpManager || '',
                  buyer_id: it.buyer_id ?? so.buyer_id ?? null,
                  buyer: it.buyer || lookedUpBuyer || '',
                  delivery_id: it.delivery_id ?? so.delivery_id ?? null,
                  factory: it.factory || lookedUpFactory || ''
                };
              });
            });
          }
        } catch (enrichError) {
          console.warn('[InvoicePrintModal] sub_order enrichment failed', enrichError);
        }

        logger.logOnce('detail fetch completed', detailResponses.map(({ invoice_id, detail }) => ({
          invoice_id,
          invoice_id_type: typeof invoice_id,
          itemCount: (nextMap[invoice_id]?.items || detail?.items || []).length,
          invoiceNo: nextMap[invoice_id]?.invoice_no || detail?.invoice_no
        })));
        setDetailedInvoiceMap(nextMap);
      } finally {
        if (isActive) {
          setIsFetchingDetails(false);
        }
      }
    };

    fetchInvoiceDetails();
    return () => {
      isActive = false;
    };
  }, [open, selectedInvoices, invoiceData]);

  useEffect(() => {
    const preloadImage = async () => {
      if (!signImageCache.current) {
        try {
          const response = await fetch(signImage);
          const blob = await response.blob();
          const objectUrl = URL.createObjectURL(blob);
          signImageCache.current = objectUrl;

          const img = new Image();
          img.src = objectUrl;
          await new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve;
          });
          setImageLoaded(true);
        } catch (error) {
          setImageLoaded(true);
        }
      } else {
        setImageLoaded(true);
      }
    };

    preloadImage();

    return () => {
      if (signImageCache.current && !open) {
        URL.revokeObjectURL(signImageCache.current);
        signImageCache.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (open) {
      setIsLoading(true);
      setIsDataReady(false);
      setCurrentPage(0);

      const printColumns = {
        customerName: true,
        invoiceNo: true,
        invoiceDate: true,
        managerName: initialColumns.manager_name === true,
        buyer: initialColumns.buyer !== false,
        styleNo: initialColumns.style_no !== false,
        fileNo: initialColumns.file_no !== false,
        factory: initialColumns.factory === true,
        deliveryDate: initialColumns.delivery_date !== false,
        // ITEM / SIZE 컬럼은 인쇄에서 항상 기본 표시
        item: true,
        size: true,
        color: initialColumns.color !== false,
        qty: initialColumns.qty !== false,
        price: initialColumns.price !== false,
        vat: initialColumns.vat === true,
        amount: initialColumns.amount !== false,
        sign: initialColumns.sign !== false
      };

      setVisibleColumns(printColumns);

      requestAnimationFrame(() => {
        setIsDataReady(true);
        setIsLoading(false);
      });
    }
  }, [open, selectedInvoices, invoiceData, initialColumns]);

  useEffect(() => {
    if (!columnSettingsOpen) return;

    const handleClickOutside = (event) => {
      if (
        columnSettingsRef.current &&
        !columnSettingsRef.current.contains(event.target) &&
        columnSettingsButtonRef.current &&
        !columnSettingsButtonRef.current.contains(event.target)
      ) {
        setColumnSettingsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [columnSettingsOpen]);

  const handlePrint = useCallback(async () => {
    if (!mergedInvoiceData || !selectedInvoices || selectedInvoices.length === 0) return;

    const signBase64 = await getSignBase64();

    // 고객사 + 담당자가 같은 인보이스들을 한 페이지로 묶어서 출력
    const groupMap = new Map();
    (selectedInvoices || []).forEach((invoiceId) => {
      const invoice = mergedInvoiceData.find((item) => item.invoice_id === invoiceId);
      if (!invoice) return;

      const customerKey =
        invoice.customer_id ??
        invoice.vendor_id ??
        invoice.customer_name ??
        invoice.vendor_name ??
        '';
      const managerKey = invoice.manager_id ?? invoice.manager_name ?? '';
      const key = `${customerKey}___${managerKey}`;

      if (!groupMap.has(key)) {
        groupMap.set(key, {
          base: invoice,
          items: []
        });
      }
      const entry = groupMap.get(key);
      entry.items.push(...(invoice.items || []));
    });

    const pages = Array.from(groupMap.values())
      .map(({ base, items }) => {
        const merged = {
          ...base,
          items
        };
        return generatePrintPageHtml(merged, visibleColumns, signBase64);
      })
      .join('');

    executePrint(PRINT_HTML_WRAPPER(pages), { downloadIndicatorRef });
  }, [mergedInvoiceData, selectedInvoices, visibleColumns]);

  const handleExcel = useCallback(async () => {
    if (downloadIndicatorRef.current?.startDownload) {
      downloadIndicatorRef.current.startDownload();
    }

    try {
      const signBase64 = await imageToBase64Raw(signImage);
      if (!mergedInvoiceData || !selectedInvoices || selectedInvoices.length === 0) {
        downloadIndicatorRef.current?.completeDownload?.();
        return;
      }

      const formatDate = (dateString) => {
        if (!dateString) return '-';
        return dayjs(dateString).format('YYYY-MM-DD');
      };

      // 고객사 + 담당자가 같은 인보이스들을 한 시트로 묶어서(=미리보기/인쇄와 동일한 기준) 단일 파일로 다운로드
      const groupMap = new Map();
      (selectedInvoices || []).forEach((invoiceId) => {
        const invoice = mergedInvoiceData.find((item) => item.invoice_id === invoiceId);
        if (!invoice) return;

        const customerKey =
          invoice.customer_id ??
          invoice.vendor_id ??
          invoice.customer_name ??
          invoice.vendor_name ??
          '';
        const managerKey = invoice.manager_id ?? invoice.manager_name ?? '';
        const key = `${customerKey}___${managerKey}`;

        if (!groupMap.has(key)) {
          groupMap.set(key, {
            base: invoice,
            items: []
          });
        }
        const entry = groupMap.get(key);
        entry.items.push(...(invoice.items || []));
      });

      const workbook = new ExcelJS.Workbook();

      const addInvoiceSheet = (sheetName, invoiceBase, items) => {
        const { totalQty, subTotal, totalVat } = computeInvoiceTotals(items);
        const grandTotal = subTotal + totalVat;
        const currencySymbol = getCurrencySymbol(invoiceBase.currency || 'USD');
        const numCorePattern = (value) => (Number.isInteger(Number(value)) ? '#,##0' : '#,##0.###');
        const moneyNumFmt = (value) => `"${currencySymbol} "${numCorePattern(value)}`;

        const safeName = String(sheetName || 'Invoice')
          .replace(/[\\/?*:[\]]/g, ' ')
          .trim()
          .slice(0, 31) || 'Invoice';

        const worksheet = workbook.addWorksheet(safeName);

        worksheet.pageSetup.paperSize = 9;
        worksheet.pageSetup.orientation = 'portrait';
        worksheet.pageSetup.fitToPage = true;
        worksheet.pageSetup.fitToWidth = 1;
        worksheet.pageSetup.fitToHeight = 0;
        worksheet.pageSetup.margins = {
          left: 0.79, right: 0.79,
          top: 0.79, bottom: 0.79,
          header: 0.3, footer: 0.3
        };

        worksheet.properties.defaultRowHeight = 20;

        const headers = [];
        let colIndex = 1;
        headers.push({ col: colIndex++, text: 'NO', width: 5 });
        if (visibleColumns.buyer !== false) headers.push({ col: colIndex++, text: 'BUYER', width: 15 });
        if (visibleColumns.styleNo !== false) headers.push({ col: colIndex++, text: 'STYLE NO', width: 15 });
        if (visibleColumns.fileNo !== false) headers.push({ col: colIndex++, text: 'FILE NO', width: 15 });
        if (visibleColumns.managerName !== false) headers.push({ col: colIndex++, text: 'MANAGER', width: 15 });
        if (visibleColumns.factory !== false) headers.push({ col: colIndex++, text: 'FACTORY', width: 15 });
        if (visibleColumns.deliveryDate !== false) headers.push({ col: colIndex++, text: 'DELIVERY DATE', width: 15 });
        if (visibleColumns.item !== false) headers.push({ col: colIndex++, text: 'ITEM', width: 25 });
        if (visibleColumns.size !== false) headers.push({ col: colIndex++, text: 'SIZE', width: 10 });
        if (visibleColumns.color !== false) headers.push({ col: colIndex++, text: 'COLOR', width: 20 });
        if (visibleColumns.qty !== false) headers.push({ col: colIndex++, text: 'QTY', width: 10 });
        if (visibleColumns.price !== false) headers.push({ col: colIndex++, text: 'PRICE', width: 15 });
        if (visibleColumns.vat !== false) headers.push({ col: colIndex++, text: 'VAT', width: 15 });
        if (visibleColumns.amount !== false) headers.push({ col: colIndex++, text: 'AMOUNT', width: 15 });

        const lastColIdx = headers.length;
        const colIdxToLetter = (idx) => {
          let n = idx;
          let letter = '';
          while (n > 0) {
            const rem = (n - 1) % 26;
            letter = String.fromCharCode(65 + rem) + letter;
            n = Math.floor((n - 1) / 26);
          }
          return letter || 'A';
        };
        const lastColLetter = colIdxToLetter(lastColIdx);
        const halfMid = Math.max(1, Math.ceil(lastColIdx / 2));
        const halfMidLetter = colIdxToLetter(halfMid);
        const rightStart = Math.min(lastColIdx, halfMid + 2);
        const rightStartLetter = colIdxToLetter(rightStart);

        worksheet.mergeCells(`A1:${lastColLetter}1`);
        worksheet.getCell('A1').value = COMPANY.name;
        worksheet.getCell('A1').font = { name: '맑은 고딕', size: 14, bold: true };
        worksheet.getCell('A1').alignment = EXCEL_CENTER_ALIGN;
        worksheet.getRow(1).height = 25;

        worksheet.mergeCells(`A2:${lastColLetter}2`);
        worksheet.getCell('A2').value = COMPANY.address;
        worksheet.getCell('A2').font = { name: '맑은 고딕', size: 10 };
        worksheet.getCell('A2').alignment = EXCEL_CENTER_ALIGN;
        worksheet.getRow(2).height = 20;

        worksheet.mergeCells(`A3:${lastColLetter}3`);
        worksheet.getCell('A3').value = COMPANY.phoneLabel;
        worksheet.getCell('A3').font = { name: '맑은 고딕', size: 10 };
        worksheet.getCell('A3').alignment = EXCEL_CENTER_ALIGN;
        worksheet.getRow(3).height = 20;

        worksheet.mergeCells(`A5:${lastColLetter}5`);
        worksheet.getCell('A5').value = 'INVOICE';
        worksheet.getCell('A5').font = { name: '맑은 고딕', size: 20, bold: true };
        worksheet.getCell('A5').alignment = EXCEL_CENTER_ALIGN;
        worksheet.getRow(5).height = 30;

        const leftAlign = { horizontal: 'left', vertical: 'middle' };
        worksheet.mergeCells(`A7:${halfMidLetter}7`);
        worksheet.getCell('A7').value = `CUSTOMER: ${invoiceBase.customer_name || '-'}`;
        worksheet.getCell('A7').font = { name: '맑은 고딕', size: 10, bold: true };
        worksheet.getCell('A7').alignment = leftAlign;

        worksheet.mergeCells(`${rightStartLetter}7:${lastColLetter}7`);
        worksheet.getCell(`${rightStartLetter}7`).value = `INVOICE NO: ${invoiceBase.invoice_no || '-'}`;
        worksheet.getCell(`${rightStartLetter}7`).font = { name: '맑은 고딕', size: 10, bold: true };
        worksheet.getCell(`${rightStartLetter}7`).alignment = leftAlign;

        worksheet.mergeCells(`A8:${halfMidLetter}8`);
        worksheet.getCell('A8').value = invoiceBase.customer_address || '-';
        worksheet.getCell('A8').font = { name: '맑은 고딕', size: 10 };
        worksheet.getCell('A8').alignment = leftAlign;

        worksheet.mergeCells(`${rightStartLetter}8:${lastColLetter}8`);
        worksheet.getCell(`${rightStartLetter}8`).value = `Issued Date: ${formatDate(invoiceBase.invoice_date)}`;
        worksheet.getCell(`${rightStartLetter}8`).font = { name: '맑은 고딕', size: 10, bold: true };
        worksheet.getCell(`${rightStartLetter}8`).alignment = leftAlign;

        worksheet.getRow(7).height = 20;
        worksheet.getRow(8).height = 20;

        const headerRow = worksheet.getRow(10);
        headerRow.height = 25;
        headers.forEach((header) => {
          const cell = headerRow.getCell(header.col);
          cell.value = header.text;
          cell.font = EXCEL_HEADER_FONT;
          cell.alignment = EXCEL_CENTER_ALIGN;
          cell.border = EXCEL_THIN_BORDER;
          worksheet.getColumn(header.col).width = header.width;
        });

        const excelUniformFs = computeUniformFontSize(items, invoiceBase, visibleColumns);
        const setExcelCell = createExcelCellSetter(excelUniformFs);

        const getColumnWidth = (colIdx) => {
          const col = worksheet.getColumn(colIdx);
          return (col && col.width) || 10;
        };
        const computeRowHeight = (values) => {
          let maxLines = 1;
          values.forEach(({ val, colIdx }) => {
            const text = String(val == null ? '' : val);
            if (!text) return;
            const width = getColumnWidth(colIdx);
            const charsPerLine = Math.max(1, Math.floor(width * 0.9));
            const lines = Math.ceil(text.length / charsPerLine);
            if (lines > maxLines) maxLines = lines;
          });
          return Math.max(24, maxLines * 14);
        };

        let currentRow = 11;
        items.forEach((item, index) => {
          const dataRow = worksheet.getRow(currentRow);
          const rowValues = [];
          const pushCell = (val, align = 'center') => {
            const colIdx = rowValues.length + 1;
            rowValues.push({ val, colIdx });
            setExcelCell(dataRow.getCell(colIdx), val, align);
          };
          const pushMoneyCell = (num) => {
            const value = Number(num || 0);
            const colIdx = rowValues.length + 1;
            rowValues.push({ val: value, colIdx });
            const cell = dataRow.getCell(colIdx);
            setExcelCell(cell, value, 'right');
            cell.numFmt = moneyNumFmt(value);
          };

          pushCell(index + 1);
          if (visibleColumns.buyer !== false) pushCell(item.buyer || '-');
          if (visibleColumns.styleNo !== false) pushCell(item.style_no || item.style_name || '-');
          if (visibleColumns.fileNo !== false) pushCell(item.file_no || '-');
          if (visibleColumns.managerName !== false) pushCell(item.manager_name || '-');
          if (visibleColumns.factory !== false) pushCell(item.factory || '-');
          if (visibleColumns.deliveryDate !== false) pushCell(formatDate(item.delivery_date || item.date));
          if (visibleColumns.item !== false) pushCell(removeSizeFromItemText(item.thread_name || item.item_description || ''));
          if (visibleColumns.size !== false) {
            const parsedSize = parseSizeFromItemText(item.thread_name || item.item_description || '');
            pushCell(parsedSize || formatSizeValue(item.size_name || item.size) || '-');
          }
          if (visibleColumns.color !== false) pushCell(item.color || '-');
          if (visibleColumns.qty !== false) pushCell(Number(item.quantity || 0));
          if (visibleColumns.price !== false) pushMoneyCell(item.price || item.unit_price || 0);
          if (visibleColumns.vat !== false) {
            const vatAmount = Number(item.vat_amount || 0) + Number(item.vat_amount2 || 0);
            pushMoneyCell(vatAmount);
          }
          if (visibleColumns.amount !== false) pushMoneyCell(item.amount || 0);

          dataRow.height = computeRowHeight(rowValues);
          currentRow++;
        });

        headers.forEach((h) => {
          const colIdx = h.col;
          let max = String(h.text || '').length;
          for (let r = 11; r < currentRow; r += 1) {
            const cell = worksheet.getRow(r).getCell(colIdx);
            const val = cell ? cell.value : null;
            let len = String(val == null ? '' : val).length;
            if (cell && cell.numFmt && typeof val === 'number') {
              len = Math.round(Math.abs(val)).toLocaleString().length + currencySymbol.length + 3;
            }
            if (len > max) max = len;
          }
          worksheet.getColumn(colIdx).width = Math.max(h.width || 5, Math.min(40, max + 2));
        });

        currentRow += 1;

        const totalCol = Math.max(1, lastColIdx - 2);
        const lightBorderBottom = { bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } } };
        const bankFont = { name: '맑은 고딕', size: 10, bold: true };
        const totalLabelFont = { name: '맑은 고딕', size: 10 };
        const vertMiddle = { vertical: 'middle' };

        const bankInfo = [
          `At bank : ${COMPANY.bank.name}`,
          `Bank Acc ($) : ${COMPANY.bank.accountUSD} SWIFT CODE : ${COMPANY.bank.swiftCode}`,
          `Bank Acc (Rp) : ${COMPANY.bank.accountIDR} SWIFT CODE : ${COMPANY.bank.swiftCode}`,
          `ACC NAME : ${COMPANY.bank.accountName}`
        ];

        const totalRows = [
          { label: 'TOTAL Q\'TY:', value: Number(totalQty || 0), numFmt: numCorePattern(Number(totalQty || 0)), font: totalLabelFont, border: lightBorderBottom },
          { label: 'SUB TOTAL:', value: Number(subTotal || 0), numFmt: moneyNumFmt(Number(subTotal || 0)), font: totalLabelFont, border: lightBorderBottom },
          { label: 'VAT:', value: Number(totalVat || 0), numFmt: moneyNumFmt(Number(totalVat || 0)), font: totalLabelFont, border: lightBorderBottom },
          { label: 'TOTAL:', value: Number(grandTotal || 0), numFmt: moneyNumFmt(Number(grandTotal || 0)), font: { name: '맑은 고딕', size: 11, bold: true }, border: { top: { style: 'thin' }, bottom: { style: 'double' } } },
        ];

        totalRows.forEach((tr, idx) => {
          const row = worksheet.getRow(currentRow);
          row.getCell(1).value = bankInfo[idx] || '';
          row.getCell(1).font = bankFont;
          row.getCell(1).alignment = vertMiddle;

          worksheet.mergeCells(currentRow, totalCol, currentRow, totalCol + 1);
          row.getCell(totalCol).value = tr.label;
          row.getCell(totalCol).font = tr.font;
          row.getCell(totalCol).alignment = { horizontal: 'left', vertical: 'middle' };
          const valueCell = row.getCell(totalCol + 2);
          valueCell.value = tr.value;
          valueCell.font = tr.font;
          valueCell.alignment = { horizontal: 'right', vertical: 'middle' };
          if (tr.numFmt) valueCell.numFmt = tr.numFmt;
          for (let c = totalCol; c <= totalCol + 2; c++) {
            row.getCell(c).border = tr.border;
          }
          currentRow++;
        });

        const showSign = signBase64 && visibleColumns.sign !== false;
        if (showSign) {
          const imageId = workbook.addImage({ base64: signBase64, extension: 'jpeg' });
          worksheet.addImage(imageId, {
            tl: { col: totalCol - 1 + 0.25, row: currentRow },
            ext: { width: 200, height: 110 }
          });
        }

        const lastRow = currentRow + (showSign ? 5 : 0);
        worksheet.pageSetup.printArea = `A1:${lastColLetter}${lastRow}`;
      };

      Array.from(groupMap.values()).forEach(({ base, items }, idx) => {
        const customer = base.customer_name || base.vendor_name || 'INVOICE';
        const manager = base.manager_name || '';
        const sheetName = groupMap.size === 1
          ? 'Invoice'
          : `${idx + 1}_${customer}${manager ? `_${manager}` : ''}`;
        addInvoiceSheet(sheetName, base, items);
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

      const nowTag = dayjs().format('YYYYMMDD_HHmm');
      const fileName = `INVOICE_PREVIEW_${nowTag}.xlsx`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      downloadIndicatorRef.current?.completeDownload?.();
    } catch (error) {
      downloadIndicatorRef.current?.completeDownload?.();
    }
  }, [mergedInvoiceData, selectedInvoices, visibleColumns]);

  const handleColumnToggle = useCallback((columnName) => {
    setVisibleColumns(prev => ({ ...prev, [columnName]: !prev[columnName] }));
  }, []);

  const toggleColumnSettings = useCallback(() => {
    setColumnSettingsOpen(prev => !prev);
  }, []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          width: '95%',
          maxWidth: '1400px',
          height: '95vh',
          maxHeight: '95vh'
        }
      }}
    >
      <DialogTitle sx={{
        m: 0,
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" fontWeight="bold">
            {t('invoice.print.preview')}
          </Typography>
          <Divider orientation="vertical" flexItem />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title={t('common.print')} arrow placement="bottom">
              <IconButton
                onClick={handlePrint}
                disabled={isLoading || isFetchingDetails}
                sx={{ color: '#2196f3', '&:hover': { backgroundColor: '#e3f2fd' } }}
              >
                <PrintIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('common.excel')} arrow placement="bottom">
              <IconButton
                onClick={handleExcel}
                disabled={isLoading || isFetchingDetails}
                sx={{ color: '#4caf50', '&:hover': { backgroundColor: '#e8f5e9' } }}
              >
                <ExcelIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Box sx={{ position: 'relative' }}>
            <Tooltip title={t('common.columnSelection')} arrow placement="bottom">
              <IconButton
                ref={columnSettingsButtonRef}
                onClick={toggleColumnSettings}
                sx={{
                  color: columnSettingsOpen ? '#2196f3' : '#6b7280',
                  backgroundColor: columnSettingsOpen ? '#e3f2fd' : 'transparent',
                  '&:hover': { backgroundColor: columnSettingsOpen ? '#bbdefb' : '#f3f4f6' }
                }}
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>
            {columnSettingsOpen && (
              <Box
                ref={columnSettingsRef}
                sx={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  mt: 1,
                  p: 2,
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: 1,
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  zIndex: 1300,
                  minWidth: '200px'
                }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                  {t('common.columnSelection')}
                </Typography>
                <Divider sx={{ mb: 1 }} />
                {COLUMN_OPTIONS.map(column => (
                  <Box key={column.id} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Checkbox
                      size="small"
                      checked={visibleColumns[column.id]}
                      onChange={() => handleColumnToggle(column.id)}
                    />
                    <Typography variant="body2">{column.label}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, position: 'relative', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {((isLoading && !isDataReady) || isFetchingDetails) ? (
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            minHeight: '400px',
            flexDirection: 'column',
            gap: 2
          }}>
            <CircularProgress size={40} />
            <Typography color="textSecondary">
              {t('common.loading')}
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{
              flex: 1,
              overflow: 'auto',
              backgroundColor: '#f5f5f5',
              p: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start'
            }}>
              <Box
                sx={{
                  backgroundColor: 'white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  width: '794px',
                  minHeight: '1123px',
                  transform: 'scale(1.05)',
                  transformOrigin: 'top center',
                  mt: 2,
                  boxSizing: 'border-box',
                  overflowY: 'auto'
                }}
              >
                <InvoicePrintTemplate
                  ref={printRef}
                  selectedInvoices={selectedInvoices}
                  invoiceData={mergedInvoiceData}
                  visibleColumns={visibleColumns}
                />
              </Box>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
        <Button onClick={onClose} variant="outlined">
          {t('common.close')}
        </Button>
      </DialogActions>

      <DownloadIndicator ref={downloadIndicatorRef} />
    </Dialog>
  );
};

export default InvoicePrintModal;
